import { useState } from 'react';

import type { SchemaChatResolver_GetChats } from 'cpool-tables/backend-resolvers';

import {
    NUMBER_SWITCH_TARGET_KINDS,
    type NumberSwitchTargetKind,
} from '~/features/whatsapp/constants/numberSwitchTarget';
import { useApplicantChats } from '~/features/whatsapp/hooks/useApplicantChats';
import { useApplicantStartChat } from '~/features/whatsapp/hooks/useApplicantStartChat';
import { useChatContextLoanRequestId } from '~/features/whatsapp/hooks/useChatContextLoanRequestId';
import { useChats } from '~/features/whatsapp/hooks/useChats';
import { useOwnDivisionPhoneNumberId } from '~/features/whatsapp/hooks/useOwnDivisionPhoneNumberId';
import { usePartnerContacts } from '~/features/whatsapp/hooks/usePartnerContacts';
import { useStartPartnerChatMutation } from '~/features/whatsapp/hooks/useStartPartnerChatMutation';
import { openPanel, useChatPanelStore } from '~/features/whatsapp/stores/chatPanelStore';
import { resolveNumberSwitchTarget } from '~/features/whatsapp/utils/resolveNumberSwitchTarget';

type Chat = SchemaChatResolver_GetChats[number];

export const useNumberSwitchAction = (chat: Chat) => {
    const ownPhoneNumberId = useOwnDivisionPhoneNumberId();
    const { data: chats = [], isError: isChatsError, isLoading: isChatsLoading } = useChats();
    const { data: partnerContacts = [], isError: isPartnerContactsError } = usePartnerContacts();
    const loanRequestId = useChatContextLoanRequestId(chat.id);
    const applicantResolution = useApplicantChats(loanRequestId);
    const applicantStartChat = useApplicantStartChat(loanRequestId);
    const startPartnerChat = useStartPartnerChatMutation();
    const leadDetailsScopeLoanRequestId = useChatPanelStore.use.leadDetailsScopeLoanRequestId() ?? undefined;
    const [isPending, setIsPending] = useState(false);

    const target = resolveNumberSwitchTarget({ applicantResolution, chat, chats, ownPhoneNumberId, partnerContacts });
    const targetKind: NumberSwitchTargetKind | undefined = target?.kind;
    const applicantStartChatForSlot = (slot: 1 | 2) => (slot === 1 ? applicantStartChat.kn1 : applicantStartChat.kn2);

    const canSwitch = (() => {
        if (isChatsError || isChatsLoading || isPartnerContactsError) return false;
        switch (target?.kind) {
            case NUMBER_SWITCH_TARGET_KINDS.OPEN_CHAT:
                return true;
            case NUMBER_SWITCH_TARGET_KINDS.START_APPLICANT_CHAT:
                return applicantStartChatForSlot(target.slot).payload != null;
            case NUMBER_SWITCH_TARGET_KINDS.START_PARTNER_CHAT:
                return startPartnerChat.canStart;
            default:
                return false;
        }
    })();

    const openStartedChat = (startedChat: Promise<{ chatId: string }>) => {
        setIsPending(true);
        startedChat
            .then(({ chatId }) => openPanel({ autoOpenTemplatePicker: true, chatId, leadDetailsScopeLoanRequestId }))
            // Command errors are already toasted by the start mutations' onError.
            .catch((error: unknown) => console.error(error))
            .finally(() => setIsPending(false));
    };

    const switchChat = () => {
        if (!target || !canSwitch || isPending) return;
        if (target.kind === NUMBER_SWITCH_TARGET_KINDS.OPEN_CHAT) {
            openPanel({ chatId: target.chatId, leadDetailsScopeLoanRequestId });
        } else if (target.kind === NUMBER_SWITCH_TARGET_KINDS.START_APPLICANT_CHAT) {
            openStartedChat(applicantStartChatForSlot(target.slot).start());
        } else {
            openStartedChat(startPartnerChat.mutateAsync({ partnerId: target.partnerId, phoneE164: target.phoneE164 }));
        }
    };

    return { canSwitch, isPending, switchChat, targetKind };
};
